import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { Fingerprint, ShieldAlert, Loader2, ScanFace } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformAvailable = async () => {
  if (!window.PublicKeyCredential) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

const errorMessage = (e) => {
  if (e?.name === 'NotAllowedError') return 'Biometric prompt was cancelled or timed out. Try again.';
    return e?.response?.data?.detail || e?.message || 'Biometric verification failed';
};

const UnsupportedDevice = ({ onCancel }) => (
  <div className="space-y-4" data-testid="passkey-unsupported">
    <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
      <ShieldAlert className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="text-sm space-y-2">
        <p className="font-semibold">This device has no biometric sign-in available</p>
        <p>Access to Youse Capital now requires Face ID, fingerprint or Windows Hello. To continue:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>iPhone/iPad: enable Face ID or Touch ID in Settings and use Safari.</li>
          <li>Android: register a fingerprint or face unlock and use Chrome.</li>
          <li>Windows: enable Windows Hello (PIN, face or fingerprint) in Settings &gt; Accounts.</li>
          <li>Mac: enable Touch ID and use Safari or Chrome.</li>
        </ul>
        <p>Or sign in from your phone. Need help? Contact support.</p>
      </div>
    </div>
    <Button variant="outline" className="w-full" onClick={onCancel} data-testid="passkey-back-btn">Back to sign in</Button>
  </div>
);

const deviceKey = (email) => `yc_passkey_device:${(email || '').trim().toLowerCase()}`;

export const PasskeyLoginStep = ({ loginTx, enrolled, email, onSuccess, onCancel }) => {
  const [supported, setSupported] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [useLocal, setUseLocal] = useState(enrolled && !!localStorage.getItem(deviceKey(email)));

  useEffect(() => { platformAvailable().then(setSupported); }, []);

  const authenticate = async () => {
    const { data: options } = await axios.post(`${API_URL}/auth/webauthn/tx/login-options`, { login_tx: loginTx });
    const credential = await startAuthentication({ optionsJSON: options });
    const { data } = await axios.post(`${API_URL}/auth/webauthn/tx/login-complete`, { login_tx: loginTx, credential });
    return data;
  };

  const register = async () => {
    const { data: options } = await axios.post(`${API_URL}/auth/webauthn/tx/register-options`, { login_tx: loginTx });
    const credential = await startRegistration({ optionsJSON: options });
    const { data } = await axios.post(`${API_URL}/auth/webauthn/tx/register-complete`, { login_tx: loginTx, credential });
    return data;
  };

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      const data = useLocal ? await authenticate() : await register();
      localStorage.setItem(deviceKey(email), '1');
      await onSuccess(data);
    } catch (e) {
      if (useLocal && e?.name === 'NotAllowedError') {
        localStorage.removeItem(deviceKey(email));
        setUseLocal(false);
        setError('Biometrics not found on this device. Tap the button to enable it here.');
      } else {
        setError(errorMessage(e));
      }
    } finally {
      setBusy(false);
    }
  };

  if (supported === null) return <Loader2 className="w-6 h-6 animate-spin mx-auto" />;
  if (!supported) return <UnsupportedDevice onCancel={onCancel} />;

  return (
    <div className="space-y-5 text-center" data-testid="passkey-step">
      <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
        {useLocal ? <Fingerprint className="w-8 h-8 text-emerald-600" /> : <ScanFace className="w-8 h-8 text-emerald-600" />}
      </div>
      <div>
        <h3 className="text-lg font-semibold" data-testid="passkey-title">
          {useLocal ? 'Confirm with biometrics' : 'Enable biometrics on this device'}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {useLocal
            ? 'Use Face ID, fingerprint or Windows Hello to access your account.'
            : 'Access now requires Face ID, fingerprint or Windows Hello. Enable it once and you are in.'}
        </p>
      </div>
      {error && <p className="text-sm text-red-600" data-testid="passkey-error">{error}</p>}
      <Button className="w-full" onClick={run} disabled={busy} data-testid="passkey-action-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Fingerprint className="w-4 h-4 mr-2" />}
        {useLocal ? 'Verify' : 'Enable biometrics and sign in'}
      </Button>
      <Button variant="ghost" className="w-full" onClick={onCancel} data-testid="passkey-cancel-btn">Cancel</Button>
    </div>
  );
};

export default PasskeyLoginStep;
